import type { Detector } from '@modules/dashboard/pages/kyc/docs-verification/docs-verification-page/providers/face-detector-provider/types';

import type { FacePipelineSource } from './face-pipeline';
import { getFacePipelineSourceSize } from './face-pipeline';

type TfLike = {
    tidy: <T>(fn: () => T) => T;
    browser: {
        fromPixels: (source: CanvasImageSource) => unknown;
    };
    image: {
        resizeBilinear: (image: unknown, size: [number, number]) => unknown;
    };
    cast: (x: unknown, dtype: string) => unknown;
    div: (a: unknown, b: unknown) => unknown;
    sub: (a: unknown, b: unknown) => unknown;
    tensor1d: (values: number[]) => unknown;
    expandDims: (x: unknown) => unknown;
    tensor: (values: number[], shape: number[]) => unknown;
    reshape: (x: unknown, shape: number[]) => unknown;
};

export interface ImageQualityResult {
    resolutionScore: number;
    lightScore: number;
    darkScore: number;
    blurScore: number;
    failedChecks: Array<'low_resolution' | 'high_illumination' | 'low_illumination' | 'blur'>;
}

export const IMG_RESOLUTION_THRESHOLD = 10000;
export const HIGH_ILLUMINATION_THRESHOLD = 0.59;
export const LOW_ILLUMINATION_THRESHOLD = 0.4;
export const BLUR_NN_THRESHOLD = 0.55;
/** Must match `iqa_mobilenetv3small100_sigmoid.tflite` input: [1, 224, 224, 3] (NHWC). */
const IQA_INPUT_SIZE = 224;

function sigmoid(x: number): number {
    const clamped = Math.max(-100, Math.min(100, x));

    return 1 / (1 + Math.exp(-clamped));
}

function makeCanvas(width: number, height: number): HTMLCanvasElement | OffscreenCanvas {
    const canvas =
        // eslint-disable-next-line no-negated-condition
        typeof OffscreenCanvas !== 'undefined'
            ? new OffscreenCanvas(width, height)
            : document.createElement('canvas');

    if (canvas instanceof HTMLCanvasElement) {
        canvas.width = width;
        canvas.height = height;
    }

    return canvas;
}

function getImageDataFromFace(
    source: FacePipelineSource,
    box: [number, number, number, number],
): ImageData | null {
    const size = getFacePipelineSourceSize(source);

    if (!size) return null;

    // Corners already rounded in computeLandmarkBox; clamp to image bounds.
    const sx = Math.max(0, Math.round(box[0]));
    const sy = Math.max(0, Math.round(box[1]));
    const ex = Math.min(size.width, Math.round(box[2]));
    const ey = Math.min(size.height, Math.round(box[3]));
    const sw = Math.max(1, ex - sx);
    const sh = Math.max(1, ey - sy);

    if (sw <= 0 || sh <= 0) return null;

    const canvas = makeCanvas(sw, sh);
    const ctx = canvas.getContext('2d') as
        | CanvasRenderingContext2D
        | OffscreenCanvasRenderingContext2D;

    ctx.drawImage(source, sx, sy, sw, sh, 0, 0, sw, sh);

    return ctx.getImageData(0, 0, sw, sh);
}

function rgbToValue(r: number, g: number, b: number): number {
    return Math.max(r, g, b);
}

function calculateIlluminationScores(faceData: ImageData): {
    lightScore: number;
    darkScore: number;
} {
    const expectedPixels = 1e5;
    const ratio = Math.sqrt(expectedPixels / (faceData.width * faceData.height));
    const rsw = Math.max(1, Math.round(faceData.width * ratio));
    const rsh = Math.max(1, Math.round(faceData.height * ratio));

    const resized = makeCanvas(rsw, rsh);
    const rctx = resized.getContext('2d') as
        | CanvasRenderingContext2D
        | OffscreenCanvasRenderingContext2D;
    const src = makeCanvas(faceData.width, faceData.height);
    const sctx = src.getContext('2d') as
        | CanvasRenderingContext2D
        | OffscreenCanvasRenderingContext2D;

    sctx.putImageData(faceData, 0, 0);
    rctx.drawImage(src as CanvasImageSource, 0, 0, rsw, rsh);

    const resizedData = rctx.getImageData(0, 0, rsw, rsh);
    const margin = 32;
    const x0 = Math.min(margin, Math.floor(rsw / 4));
    const y0 = Math.min(margin, Math.floor(rsh / 4));
    const x1 = Math.max(x0 + 1, rsw - x0);
    const y1 = Math.max(y0 + 1, rsh - y0);

    let lightPixels = 0;
    let darkPixels = 0;
    let total = 0;

    for (let y = y0; y < y1; y++) {
        for (let x = x0; x < x1; x++) {
            const i = (y * rsw + x) * 4;
            const v = rgbToValue(
                resizedData.data[i],
                resizedData.data[i + 1],
                resizedData.data[i + 2],
            );

            if (v >= 230) lightPixels += 1;
            if (v <= 45) darkPixels += 1;
            total += 1;
        }
    }

    if (!total) {
        return { lightScore: 0, darkScore: 0 };
    }

    return {
        lightScore: lightPixels / total,
        darkScore: darkPixels / total,
    };
}

function readFirstScalar(output: unknown): number {
    type TensorLike = { dataSync?: () => Float32Array; dispose?: () => void };
    const out = output as TensorLike | TensorLike[];

    if (Array.isArray(out) && out.length && typeof out[0]?.dataSync === 'function') {
        const tensor = out[0];
        const v = tensor.dataSync ? (tensor.dataSync()[0] ?? 0) : 0;

        out.forEach((t) => t?.dispose?.());

        return v;
    }

    if (!Array.isArray(out) && out && typeof out.dataSync === 'function') {
        const v = out.dataSync()[0] ?? 0;

        out.dispose?.();

        return v;
    }

    return 0;
}

function runBlurModel(tf: TfLike, model: Detector, faceData: ImageData): number {
    const canvas = makeCanvas(faceData.width, faceData.height);
    const ctx = canvas.getContext('2d') as
        | CanvasRenderingContext2D
        | OffscreenCanvasRenderingContext2D;

    ctx.putImageData(faceData, 0, 0);

    const rawScore = tf.tidy(() => {
        const image = tf.browser.fromPixels(canvas as CanvasImageSource);
        const resized = tf.image.resizeBilinear(image, [IQA_INPUT_SIZE, IQA_INPUT_SIZE]);
        const scaled = tf.div(tf.cast(resized, 'float32'), 255.0);
        const mean = tf.tensor1d([0.485, 0.456, 0.406]);
        const std = tf.tensor1d([0.229, 0.224, 0.225]);
        const normalized = tf.div(tf.sub(scaled, mean), std);
        const input = tf.expandDims(normalized);
        const output = model.predict(input);

        return readFirstScalar(output);
    });

    return rawScore >= 0 && rawScore <= 1 ? rawScore : sigmoid(rawScore);
}

export function runImageQualityPipeline(
    model: Detector,
    source: FacePipelineSource,
    box: [number, number, number, number],
): ImageQualityResult | null {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const tf = (window as any).tf as TfLike | undefined;
    const size = getFacePipelineSourceSize(source);

    if (!tf || !model || !size) return null;

    const faceData = getImageDataFromFace(source, box);

    if (!faceData) return null;

    const resolutionScore = faceData.width * faceData.height;
    const { lightScore, darkScore } = calculateIlluminationScores(faceData);
    const blurScore = runBlurModel(tf, model, faceData);

    const failedChecks: ImageQualityResult['failedChecks'] = [];

    if (resolutionScore < IMG_RESOLUTION_THRESHOLD) failedChecks.push('low_resolution');
    if (lightScore > HIGH_ILLUMINATION_THRESHOLD) failedChecks.push('high_illumination');
    if (darkScore > LOW_ILLUMINATION_THRESHOLD) failedChecks.push('low_illumination');
    if (blurScore > BLUR_NN_THRESHOLD) failedChecks.push('blur');

    return {
        resolutionScore,
        lightScore,
        darkScore,
        blurScore,
        failedChecks,
    };
}
